import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, Zap, Clock, ShieldCheck } from "lucide-react";
import { TEL_LINK, WA_LINK, PHONE_FORMATTED } from "../constants/contact";
import { OpenStatus } from "./OpenStatus";

const WhatsAppIcon = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413" />
  </svg>
);

const RevealLine = ({ children, delay = 0 }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const STATS = [
  { icon: Clock, label: "7/24 ACİL SERVİS" },
  { icon: Zap, label: "AYNI GÜN MÜDAHALE" },
  { icon: ShieldCheck, label: "GARANTİLİ İŞÇİLİK" },
];

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const glowY = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-screen flex items-center overflow-hidden pt-28 pb-24 sm:pt-32"
    >
      <div className="absolute inset-0 bg-grid" aria-hidden="true" />
      <motion.div
        style={{ y: glowY }}
        aria-hidden="true"
        className="absolute -top-40 -right-40 w-[560px] h-[560px] rounded-full bg-amber-500/15 blur-[140px]"
      />
      <div
        aria-hidden="true"
        className="absolute bottom-0 left-1/4 w-[420px] h-[420px] rounded-full bg-cyan-400/8 blur-[120px]"
      />

      <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full grid lg:grid-cols-[1.15fr_0.85fr] gap-14 lg:gap-10 items-center">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            data-testid="hero-status-badge"
            className="inline-flex items-center gap-2.5 rounded-full border border-cyan-400/30 bg-cyan-400/5 px-4 py-2 mb-8"
          >
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-400" />
            </span>
            <span className="font-mono text-[11px] tracking-[0.2em] text-cyan-300 uppercase">
              7/24 Beylikdüzü bölgesinde aktif usta
            </span>
          </motion.div>

          <h1
            data-testid="hero-headline"
            className="font-display font-extrabold tracking-tight leading-[1.04] text-4xl sm:text-6xl lg:text-7xl text-slate-50"
          >
            <RevealLine delay={0.1}>BEYLİKDÜZÜ'NÜN</RevealLine>
            <RevealLine delay={0.22}>
              <span className="text-amber-400 drop-shadow-[0_0_28px_rgba(245,158,11,0.35)]">
                USTA ELEKTRİKÇİSİ
              </span>
            </RevealLine>
            <RevealLine delay={0.34}>KAPINIZDA.</RevealLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mt-6 max-w-xl text-base sm:text-lg text-slate-400 leading-relaxed"
          >
            Arıza, tesisat, avize, priz, sigorta — net fiyat, garantili iş.
            Aracı yok, çağrı merkezi yok. Telefonu doğrudan işi yapacak
            usta açar.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
            className="mt-9 flex flex-col sm:flex-row gap-4"
          >
            <a
              href={TEL_LINK}
              data-testid="hero-call-button"
              className="group inline-flex items-center justify-center gap-3 rounded-full bg-amber-500 hover:bg-amber-400 text-black font-display font-bold text-base sm:text-lg px-8 py-4 transition-all duration-200 shadow-[0_0_36px_rgba(245,158,11,0.4)] hover:shadow-[0_0_48px_rgba(245,158,11,0.55)]"
            >
              <Phone className="w-5 h-5 transition-transform duration-200 group-hover:-rotate-12" strokeWidth={2.5} />
              HEMEN ARA: {PHONE_FORMATTED}
            </a>
            <a
              href={WA_LINK}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="inline-flex items-center justify-center gap-3 rounded-full bg-green-500 hover:bg-green-400 text-black font-display font-bold text-base sm:text-lg px-8 py-4 transition-all duration-200 shadow-[0_0_30px_rgba(34,197,94,0.3)]"
            >
              <WhatsAppIcon className="w-5 h-5" />
              WHATSAPP İLE YAZ
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-10 flex flex-wrap gap-x-8 gap-y-3"
            data-testid="hero-stats"
          >
            {STATS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2.5">
                <Icon className="w-4 h-4 text-amber-400" strokeWidth={2.25} />
                <span className="font-mono text-[11px] tracking-[0.18em] text-slate-400 uppercase">
                  {label}
                </span>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="relative hidden lg:block"
        >
          <motion.div style={{ y: imgY }} className="relative">
            <div className="absolute -inset-3 rounded-[28px] border border-amber-500/25 rotate-2" aria-hidden="true" />
            <div className="relative rounded-[24px] overflow-hidden border border-slate-700/60 shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1621905251189-08b45d6a269e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxlbGVjdHJpY2lhbiUyMHJlcGFpcmluZyUyMHdpcmVzJTIwd29ya3xlbnwwfHx8fDE3OTM3Nzk1NTd8MA&ixlib=rb-4.1.0&q=85"
                alt="Elektrik ustası pano üzerinde çalışıyor"
                data-testid="hero-image"
                className="w-full h-[520px] object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#070A0F]/80 via-transparent to-transparent" />
            </div>
            <div className="absolute -bottom-5 -left-5 rounded-2xl bg-[#0F172A]/95 backdrop-blur border border-slate-700 px-5 py-4 shadow-xl">
              <div className="mb-1.5">
                <OpenStatus compact />
              </div>
              <p className="font-display font-bold text-slate-50 text-lg leading-none">
                Hemen Ara, Bugün Gelsin
              </p>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};
